import { useCallback, useEffect, useMemo, useState } from 'react'
import { PublicPaymentWidget } from './PublicPaymentWidget'
import {
  publicTreatmentPlanErrorSchema,
  publicTreatmentPlanResponseSchema,
  type PublicTreatmentPlan,
  type PublicPlanService,
  type PublicPlanStage,
} from '@unified-lk/api-contracts'
import type { TenantBootstrapModel } from '@unified-lk/tenant-config'
import { ToothChart } from './ToothChart'

function resolvePublicTheme(plan?: PublicTreatmentPlan | null): 'blue' | 'gold' {
  const theme = plan?.metadata?.public_theme

  return theme === 'gold' ? 'gold' : 'blue'
}

function resolveAccent(bootstrap: TenantBootstrapModel, plan?: PublicTreatmentPlan | null): string {
  if (resolvePublicTheme(plan) === 'gold') {
    return bootstrap.clinic?.publicAccentColor ?? '#c6ac4a'
  }

  return bootstrap.branding.primaryColor || '#1aa8d6'
}

function formatDate(value?: string | null): string {
  if (!value) return 'Не указано'
  try {
    return new Date(value).toLocaleDateString('ru-RU')
  } catch {
    return value
  }
}

function formatCurrency(value?: number | null): string {
  if (value === null || value === undefined || Number.isNaN(value)) return 'По запросу'
  return new Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'RUB', maximumFractionDigits: 0 }).format(value)
}

function resolveOfferDeadline(plan?: PublicTreatmentPlan | null): string | null {
  const direct = (plan as (PublicTreatmentPlan & { offer_deadline?: string | null }) | null | undefined)?.offer_deadline
  if (typeof direct === 'string' && direct.trim() !== '') return direct
  const metadataDeadline = plan?.metadata?.offer_deadline
  return typeof metadataDeadline === 'string' && metadataDeadline.trim() !== '' ? metadataDeadline : null
}

function currentScrollDepth(): number {
  const doc = document.documentElement
  const scrollable = Math.max(doc.scrollHeight - window.innerHeight, 1)
  return Math.max(0, Math.min(100, Math.round((window.scrollY / scrollable) * 100)))
}

function sendPublicEvent(apiBase: string, token: string, event: string, payload: Record<string, unknown> = {}) {
  const body = JSON.stringify({
    event,
    user_agent: navigator.userAgent,
    device_type: /Mobi|Android/i.test(navigator.userAgent) ? 'mobile' : 'desktop',
    ...payload,
  })
  for (const kind of ['constructor-plans', 'plans']) {
    const url = `${apiBase}/public/${kind}/${encodeURIComponent(token)}/events`
    if (event === 'close' && navigator.sendBeacon) {
      navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }))
      continue
    }
    void fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body,
      keepalive: event === 'close',
    }).catch(() => undefined)
  }
}

function statusLabel(value?: string | null): string {
  if (!value) return 'Запланировано'
  if (value === 'active') return 'Активный'
  if (value === 'completed') return 'Завершён'
  if (value === 'archived') return 'Архив'
  if (value === 'expired') return 'Срок ссылки истёк'
  if (value === 'inactive') return 'Ссылка деактивирована'
  if (value === 'draft') return 'Черновик'
  return value
}

function PublicPlanUnavailable({
  bootstrap,
}: {
  bootstrap: TenantBootstrapModel
}) {
  return (
    <section className="mx-auto max-w-2xl rounded-3xl border border-gray-200 bg-white p-8 text-center shadow-sm">
      <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-amber-50 text-amber-600">
        <svg className="h-7 w-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v4m0 4h.01M5.07 19h13.86A2 2 0 0020.66 16L13.73 4a2 2 0 00-3.46 0L3.34 16a2 2 0 001.73 3z" />
        </svg>
      </div>
      <h1 className="text-2xl font-bold text-gray-900">Ссылка не активна</h1>
      <p className="mt-3 text-base leading-7 text-gray-600">
        Пожалуйста, обратитесь в клинику для активации ссылки.
      </p>
      <div className="mt-6 flex flex-wrap items-center justify-center gap-3 text-sm">
        {bootstrap.clinic?.phone ? (
          <a className="rounded-full bg-slate-900 px-4 py-2 font-medium text-white" href={`tel:${bootstrap.clinic.phoneTel}`}>
            {bootstrap.clinic.phone}
          </a>
        ) : null}
        {bootstrap.clinic?.email ? (
          <a className="rounded-full border border-gray-200 px-4 py-2 font-medium text-gray-700" href={`mailto:${bootstrap.clinic.email}`}>
            {bootstrap.clinic.email}
          </a>
        ) : null}
      </div>
    </section>
  )
}

function normalizePlanFromConstructorPayload(data: Record<string, unknown>): PublicTreatmentPlan {
  const shareCandidate = (data.public && typeof data.public === 'object')
    ? data.public as Record<string, unknown>
    : null
  const metadata = (data.metadata && typeof data.metadata === 'object')
    ? { ...(data.metadata as Record<string, unknown>) }
    : {}
  const publicTheme = typeof data.public_theme === 'string' && data.public_theme === 'gold' ? 'gold' : 'blue'
  metadata.public_theme = typeof metadata.public_theme === 'string' ? metadata.public_theme : publicTheme

  const stages = Array.isArray(data.stages) ? data.stages : []

  return {
    id: typeof data.id === 'number' ? data.id : 0,
    title: typeof data.title === 'string' ? data.title : 'План лечения',
    patient_id: typeof data.patient_id === 'number' ? data.patient_id : null,
    patient_name: typeof data.patient_name === 'string' ? data.patient_name : undefined,
    doctor_id: typeof data.doctor_id === 'number' ? data.doctor_id : null,
    doctor_name: typeof data.doctor_name === 'string' ? data.doctor_name : undefined,
    description: typeof data.description === 'string' ? data.description : null,
    status: typeof data.status === 'string' ? data.status : 'active',
    total_amount: typeof data.total_amount === 'number' ? data.total_amount : null,
    total_services: typeof data.total_services === 'number' ? data.total_services : null,
    completed_services: typeof data.completed_services === 'number' ? data.completed_services : null,
    progress: typeof data.progress === 'number' ? data.progress : null,
    plan_date: typeof data.plan_date === 'string' ? data.plan_date : null,
    created_at: typeof data.created_at === 'string' ? data.created_at : null,
    updated_at: typeof data.updated_at === 'string' ? data.updated_at : null,
    hide_prices: Boolean(data.hide_prices ?? shareCandidate?.hide_prices ?? false),
    offer_deadline: typeof data.offer_deadline === 'string'
      ? data.offer_deadline
      : (typeof metadata.offer_deadline === 'string' ? metadata.offer_deadline : null),
    public_secret: typeof data.public_secret === 'string' ? data.public_secret : null,
    public_link_active: Boolean(data.public_link_active ?? shareCandidate?.active ?? false),
    public_link_expires_at: typeof data.public_link_expires_at === 'string'
      ? data.public_link_expires_at
      : (typeof shareCandidate?.expires_at === 'string' ? shareCandidate.expires_at : null),
    share: shareCandidate ? {
      public_token: typeof shareCandidate.token === 'string' ? shareCandidate.token : null,
      public_url: typeof shareCandidate.url === 'string' ? shareCandidate.url : null,
      is_active: Boolean(shareCandidate.active),
      expires_at: typeof shareCandidate.expires_at === 'string' ? shareCandidate.expires_at : null,
      hide_prices: Boolean(shareCandidate.hide_prices ?? false),
      allow_materials: false,
      view_count: null,
      print_count: null,
    } : undefined,
    view_count: typeof data.view_count === 'number' ? data.view_count : null,
    print_count: typeof data.print_count === 'number' ? data.print_count : null,
    allow_materials: Boolean(data.allow_materials ?? false),
    stages: stages.map((stage, stageIndex) => {
      const row = (stage && typeof stage === 'object') ? stage as Record<string, unknown> : {}
      const servicesRaw = Array.isArray(row.services) ? row.services : []
      const cashOffer = parseCashOffer(row.cash_offer)
      return {
        id: typeof row.id === 'number' ? row.id : stageIndex + 1,
        title: typeof row.title === 'string' ? row.title : `Этап ${stageIndex + 1}`,
        description: typeof row.description === 'string' ? row.description : null,
        status: typeof row.status === 'string' ? row.status : 'active',
        total_amount: typeof row.total_amount === 'number'
          ? row.total_amount
          : typeof row.amount === 'number' ? row.amount : null,
        amount_with_discount: typeof row.amount_with_discount === 'number' ? row.amount_with_discount : null,
        amount_savings: typeof row.amount_savings === 'number' ? row.amount_savings : null,
        doctor_id: typeof row.doctor_id === 'number' ? row.doctor_id : null,
        doctor_name: typeof row.doctor_name === 'string' ? row.doctor_name : null,
        comment: typeof row.comment === 'string' ? row.comment : null,
        cash_offer: cashOffer,
        services: servicesRaw.map((service, serviceIndex) => {
          const svc = (service && typeof service === 'object') ? service as Record<string, unknown> : {}
          return {
            id: typeof svc.id === 'number' ? svc.id : serviceIndex + 1,
            service_name: typeof svc.service_name === 'string' ? svc.service_name : 'Услуга',
            tooth_number: typeof svc.tooth_number === 'string' ? svc.tooth_number : null,
            quantity: typeof svc.quantity === 'number' ? svc.quantity : 1,
            unit_price: typeof svc.unit_price === 'number' ? svc.unit_price : null,
            original_unit_price: typeof svc.original_unit_price === 'number' ? svc.original_unit_price : null,
            total_price: typeof svc.total_price === 'number' ? svc.total_price : null,
            live_unit_price: typeof svc.live_unit_price === 'number' ? svc.live_unit_price : null,
            live_discounted_price: typeof svc.live_discounted_price === 'number' ? svc.live_discounted_price : null,
            live_total_price: typeof svc.live_total_price === 'number' ? svc.live_total_price : null,
            live_price_diff: typeof svc.live_price_diff === 'number' ? svc.live_price_diff : null,
            status: typeof svc.status === 'string' ? svc.status : null,
          }
        }),
      }
    }) as PublicPlanStage[],
    media: [],
    installment: parseInstallment(data.installment),
    note: typeof data.note === 'string' ? data.note : (typeof metadata.note === 'string' ? metadata.note : null),
    plan_cash_offer: parseCashOffer((data.plan_cash_offer ?? metadata.plan_cash_offer) as Record<string, unknown> | null | undefined),
    pricing_summary: (data.pricing_summary && typeof data.pricing_summary === 'object')
      ? data.pricing_summary as PublicTreatmentPlan['pricing_summary']
      : null,
    custom_fields: [],
    metadata,
    payment: (data.payment && typeof data.payment === 'object')
      ? data.payment as PublicTreatmentPlan['payment']
      : null,
  }
}

interface GroupedRow {
  name: string
  teeth: string[]
  unitPrice: number
  discountedUnitPrice: number
  quantity: number
}

export interface StageCashOffer {
  amount?: number | null
  note?: string | null
  deadline?: string | null
  method?: string | null
}

function parseCashOffer(raw: unknown): StageCashOffer | null {
  if (!raw || typeof raw !== 'object') return null
  const r = raw as Record<string, unknown>
  const amount = typeof r.amount === 'number'
    ? r.amount
    : typeof r.price === 'number' ? r.price : null
  const note = typeof r.note === 'string'
    ? r.note
    : typeof r.text === 'string' ? r.text : null
  const deadline = typeof r.deadline === 'string'
    ? r.deadline
    : typeof r.until === 'string' ? r.until : null
  const method = typeof r.method === 'string' ? r.method : null
  if (amount == null && !note && !deadline) return null
  return { amount, note, deadline, method }
}

function parseInstallment(raw: unknown): PublicTreatmentPlan['installment'] {
  if (!raw || typeof raw !== 'object') return null
  const r = raw as Record<string, unknown>
  const scheduleRaw = Array.isArray(r.schedule) ? r.schedule : []
  const schedule = scheduleRaw
    .map((item, index) => {
      const row = (item && typeof item === 'object') ? item as Record<string, unknown> : {}
      const title = typeof row.title === 'string' && row.title.trim() !== '' ? row.title : `Платёж ${index + 1}`
      return {
        id: typeof row.id === 'number' ? row.id : null,
        stage_id: typeof row.stage_id === 'number' ? row.stage_id : null,
        stage_title: typeof row.stage_title === 'string' ? row.stage_title : null,
        title,
        amount: typeof row.amount === 'number' ? row.amount : null,
        due_date: typeof row.due_date === 'string' ? row.due_date : null,
        sort_order: typeof row.sort_order === 'number' ? row.sort_order : index,
        status: typeof row.status === 'string' ? row.status : 'planned',
        note: typeof row.note === 'string' ? row.note : null,
      }
    })
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))

  return {
    enabled: Boolean(r.enabled),
    apply_to: typeof r.apply_to === 'string' ? r.apply_to : null,
    stage_id: typeof r.stage_id === 'number' ? r.stage_id : null,
    months: typeof r.months === 'number' ? r.months : null,
    amount: typeof r.amount === 'number' ? r.amount : null,
    rate: typeof r.rate === 'number' ? r.rate : null,
    start_date: typeof r.start_date === 'string' ? r.start_date : null,
    note: typeof r.note === 'string' ? r.note : null,
    schedule,
  }
}

function buildGroupedRows(services: PublicPlanService[]): GroupedRow[] {
  const map = new Map<string, GroupedRow>()
  for (const service of services) {
    const quantity = service.quantity ?? 1
    const currentUnitPrice = service.total_price !== null && service.total_price !== undefined && quantity > 0
      ? service.total_price / quantity
      : (service.unit_price ?? 0)
    const originalUnitPrice = (service as { original_unit_price?: number | null }).original_unit_price ?? null
    const unitPrice = originalUnitPrice !== null && originalUnitPrice > currentUnitPrice
      ? originalUnitPrice
      : (service.unit_price ?? service.total_price ?? 0)
    const explicitDiscount = (service as { discounted_unit_price?: number | null }).discounted_unit_price ?? null
    const liveDiscount =
      (service as { live_discounted_price?: number | null }).live_discounted_price ??
      (service as { live_unit_price?: number | null }).live_unit_price ??
      null
    const candidates = [explicitDiscount, liveDiscount].filter(
      (value): value is number => typeof value === 'number' && value < unitPrice && value > 0,
    )
    const discountedUnitPrice = candidates.length > 0 ? Math.min(...candidates) : unitPrice
    const hasCoefficient = discountedUnitPrice < unitPrice
    const rawTooth = (service.tooth_number ?? '').trim()
    const tooth = (rawTooth === '' || rawTooth === '-' || rawTooth === '—' || rawTooth === '0') ? '' : rawTooth
    const key = hasCoefficient
      ? `${service.id}__${service.service_name}__${unitPrice}__${discountedUnitPrice}`
      : `${service.service_name}__${unitPrice}`
    const existing = map.get(key)
    if (existing) {
      if (tooth !== '' && !existing.teeth.includes(tooth)) {
        existing.teeth.push(tooth)
      } else if (tooth === '' && existing.teeth.length === 0) {
        existing.teeth.push('Вся полость')
      }
      existing.quantity += service.quantity ?? 1
    } else {
      map.set(key, {
        name: service.service_name,
        teeth: tooth === '' ? ['Вся полость'] : [tooth],
        unitPrice,
        discountedUnitPrice,
        quantity,
      })
    }
  }
  return Array.from(map.values())
}

function collectTeeth(stages: PublicPlanStage[]): { display: string[]; numeric: number[]; servicesByTooth: Record<number, string[]> } {
  const display = new Set<string>()
  const numeric = new Set<number>()
  const servicesByTooth: Record<number, string[]> = {}
  for (const stage of stages) {
    for (const service of stage.services) {
      const tooth = (service.tooth_number ?? '').trim()
      if (!tooth || tooth === '-' || tooth === '—' || tooth === '0' || tooth.toLowerCase() === 'вся полость') continue
      display.add(tooth)
      const num = Number.parseInt(tooth, 10)
      if (Number.isFinite(num) && num > 0) {
        numeric.add(num)
        const label = service.service_name
        const current = servicesByTooth[num] ?? []
        if (!current.includes(label)) {
          servicesByTooth[num] = [...current, label]
        }
      }
    }
  }
  const displayArr = Array.from(display).sort((a, b) => Number(a) - Number(b))
  const numericArr = Array.from(numeric).sort((a, b) => a - b)
  return { display: displayArr, numeric: numericArr, servicesByTooth }
}

export function PublicTreatmentPlanPage({
  token,
  bootstrap,
}: {
  token: string
  bootstrap: TenantBootstrapModel
}) {
  const [plan, setPlan] = useState<PublicTreatmentPlan | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [linkStatus, setLinkStatus] = useState<string | null>(null)
  // Password gate
  const [needsPassword, setNeedsPassword] = useState(false)
  const [passwordInput, setPasswordInput] = useState('')
  const [passwordError, setPasswordError] = useState('')
  const [passwordChecking, setPasswordChecking] = useState(false)

  const endpoints = useMemo(
    () => [
      `${bootstrap.apiBase}/public/plans/${encodeURIComponent(token)}`,
      `${bootstrap.apiBase}/public/constructor-plans/${encodeURIComponent(token)}`,
    ],
    [bootstrap.apiBase, token],
  )

  const tryLoad = useCallback(async (password?: string) => {
    let lastError = 'Публичный план недоступен.'
    let lastStatus: string | null = null

    for (const baseEndpoint of endpoints) {
      const endpoint = password
        ? `${baseEndpoint}?password=${encodeURIComponent(password)}`
        : baseEndpoint
      let response: Response
      let json: unknown
      try {
        response = await fetch(endpoint, { headers: { Accept: 'application/json' } })
      } catch (fetchErr) {
        lastError = fetchErr instanceof Error ? fetchErr.message : 'Не удалось подключиться к серверу.'
        continue
      }
      try { json = await response.json() } catch { lastError = 'Сервер вернул некорректный ответ.'; continue }

      if (!response.ok) {
        const parsedError = publicTreatmentPlanErrorSchema.safeParse(json)
        if (parsedError.success) {
          lastError = parsedError.data.error
          lastStatus = parsedError.data.status
        } else {
          const raw = json as Record<string, unknown>
          lastStatus = raw.status as string ?? lastStatus
        }
        if (lastStatus === 'password_required') {
          return { passwordRequired: true }
        }
        continue
      }

        const parsed = publicTreatmentPlanResponseSchema.safeParse(json)
        if (parsed.success) {
          return { plan: parsed.data.data }
        }

        if (typeof json === 'object' && json !== null) {
          const payload = json as Record<string, unknown>
          const dataCandidate = payload.data
          if (dataCandidate && typeof dataCandidate === 'object') {
            return { plan: normalizePlanFromConstructorPayload(dataCandidate as Record<string, unknown>) }
          }
        }
      }

      return { error: lastError || 'Не удалось прочитать публичный план.', status: lastStatus }
  }, [endpoints])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    setLinkStatus(null)
    setPlan(null)
    setNeedsPassword(false)

    void tryLoad()
      .then((result) => {
        if (cancelled) return
        if (!result) return
        if ('passwordRequired' in result) { setNeedsPassword(true); return }
        if ('plan' in result && result.plan) { setPlan(result.plan as PublicTreatmentPlan); return }
        if ('error' in result) { setError(result.error as string); setLinkStatus(result.status as string | null) }
      })
      .catch((err) => {
        if (!cancelled) {
          setLinkStatus(null)
          setError(err instanceof Error ? `Ошибка загрузки: ${err.message}` : 'Ошибка загрузки публичного плана.')
        }
      })
      .finally(() => { if (!cancelled) setLoading(false) })

    return () => {
      cancelled = true
    }
  }, [endpoints])

  useEffect(() => {
    if (!plan || !token) return

    const startedAt = Date.now()
    let maxScrollDepth = currentScrollDepth()
    sendPublicEvent(bootstrap.apiBase, token, 'view', { scroll_depth_percent: maxScrollDepth })

    const handleScroll = () => {
      maxScrollDepth = Math.max(maxScrollDepth, currentScrollDepth())
    }
    const handleBeforeUnload = () => {
      sendPublicEvent(bootstrap.apiBase, token, 'close', {
        time_on_page_ms: Date.now() - startedAt,
        scroll_depth_percent: maxScrollDepth,
      })
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      handleBeforeUnload()
      window.removeEventListener('scroll', handleScroll)
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [bootstrap.apiBase, plan, token])

  const recordEvent = useCallback((event: string, payload: Record<string, unknown> = {}) => {
    sendPublicEvent(bootstrap.apiBase, token, event, payload)
  }, [bootstrap.apiBase, token])

  async function handlePasswordSubmit(e: React.FormEvent) {
    e.preventDefault()
    const pw = passwordInput.trim()
    if (!pw) { setPasswordError('Введите пароль'); return }
    setPasswordChecking(true)
    setPasswordError('')
    try {
      const result = await tryLoad(pw)
      if (!result) { setPasswordError('Не удалось загрузить план.'); return }
      if ('passwordRequired' in result) { setPasswordError('Неверный пароль. Попробуйте ещё раз.'); return }
      if ('plan' in result && result.plan) {
        setNeedsPassword(false)
        setPasswordInput('')
        setPlan(result.plan as PublicTreatmentPlan)
        return
      }
      setPasswordError((result as { error?: string }).error ?? 'Не удалось загрузить план.')
    } catch {
      setPasswordError('Не удалось загрузить план.')
    } finally {
      setPasswordChecking(false)
    }
  }

  const accent = resolveAccent(bootstrap, plan)
  const publicThemeStyle = {
    '--public-primary': bootstrap.branding.primaryColor || '#1aa8d6',
    '--public-accent': accent,
    '--public-accent-soft': `color-mix(in srgb, ${accent} 10%, white)`,
    '--public-accent-softer': `color-mix(in srgb, ${accent} 5%, white)`,
    '--public-accent-line': `color-mix(in srgb, ${accent} 35%, #dbeafe)`,
    '--public-surface': '#ffffff',
    '--public-bg': '#f6f9fd',
  } as React.CSSProperties
  const accentBg = { backgroundColor: 'var(--public-accent)' }
  const accentLight = { backgroundColor: 'var(--public-accent-soft)' }
  const accentLighter = { backgroundColor: 'var(--public-accent-softer)' }
  const accentBorder = { borderColor: 'var(--public-accent-line)' }
  const accentColor = { color: 'var(--public-accent)' }

  return (
    <div className="min-h-screen bg-[var(--public-bg)]" style={{ fontFamily: 'Roboto, system-ui, -apple-system, sans-serif', ...publicThemeStyle }}>
      <header className="bg-white border-b border-gray-200 shadow-sm print:shadow-none">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between gap-4">
            <a
              href={bootstrap.clinic?.website ?? '#'}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3"
            >
              {bootstrap.clinic?.publicLogoUrl ? (
                <img
                  src={bootstrap.clinic.publicLogoUrl}
                  alt={bootstrap.branding.appName}
                  className="h-10 w-auto md:h-12"
                  style={{ maxHeight: 57, maxWidth: 254 }}
                />
              ) : (
                <span className="text-xl font-bold text-gray-900">{bootstrap.branding.appName}</span>
              )}
            </a>
            <div className="hidden md:flex items-center gap-6">
              {bootstrap.clinic?.phone && (
                <a
                  href={`tel:${bootstrap.clinic.phoneTel}`}
                  className="flex items-center gap-2 text-gray-700 transition-colors hover:opacity-80"
                  style={accentColor}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  <span className="text-sm font-medium">{bootstrap.clinic.phone}</span>
                </a>
              )}
              {bootstrap.clinic?.email && (
                <a
                  href={`mailto:${bootstrap.clinic.email}`}
                  className="flex items-center gap-2 text-gray-700 transition-colors hover:opacity-80"
                  style={accentColor}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <span className="text-sm font-medium">{bootstrap.clinic.email}</span>
                </a>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="no-print mb-6 text-right">
          <button
            type="button"
            onClick={() => {
              recordEvent('print', { target: 'print_button' })
              window.print()
            }}
            className="text-white px-6 py-2.5 rounded-lg font-medium hover:opacity-90 transition-opacity"
            style={accentBg}
          >
            Печать
          </button>
        </div>

        {loading ? (
          <section className="rounded-xl border border-gray-200 bg-white p-8 text-sm text-gray-500 shadow-sm">
            Загрузка плана...
          </section>
        ) : needsPassword ? (
          <section className="mx-auto w-full max-w-sm rounded-2xl border border-gray-200 bg-white p-8 shadow-sm">
            <div className="mb-6 flex flex-col items-center gap-3 text-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-full" style={{ background: `${accent}18` }}>
                <svg className="h-7 w-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: accent }}>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-gray-900">Доступ по паролю</h2>
              <p className="text-sm leading-5 text-gray-500">
                Врач ограничил доступ к этому плану лечения.<br />Введите пароль, чтобы открыть план.
              </p>
            </div>
            <form onSubmit={(e) => void handlePasswordSubmit(e)} className="grid gap-3">
              <input
                type="password"
                autoFocus
                autoComplete="current-password"
                className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-gray-900 outline-none transition focus:border-sky-300 focus:ring-2 focus:ring-sky-100"
                placeholder="Пароль"
                value={passwordInput}
                onChange={(e) => { setPasswordInput(e.target.value); setPasswordError('') }}
                disabled={passwordChecking}
              />
              {passwordError && (
                <p className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">{passwordError}</p>
              )}
              <button
                type="submit"
                disabled={passwordChecking || !passwordInput.trim()}
                className="w-full rounded-xl py-3 text-sm font-semibold text-white transition disabled:opacity-50"
                style={{ background: accent }}
              >
                {passwordChecking ? 'Проверяем...' : 'Открыть план'}
              </button>
            </form>
          </section>
        ) : error ? (
          linkStatus === 'inactive' || linkStatus === 'expired' ? (
            <PublicPlanUnavailable bootstrap={bootstrap} />
          ) : (
          <section className="rounded-xl border border-rose-300 bg-white p-8 text-sm text-rose-700 shadow-sm">
            {error}
          </section>
          )
        ) : plan ? (
          <PlanContent
            plan={plan}
            bootstrap={bootstrap}
            onPublicEvent={recordEvent}
            accent={accent}
            accentBg={accentBg}
            accentLight={accentLight}
            accentLighter={accentLighter}
            accentBorder={accentBorder}
            accentColor={accentColor}
          />
        ) : null}
      </main>

      {/* Payment widget — shown only if share has payment providers configured */}
      {plan?.payment && (plan.payment.providers?.yookassa || plan.payment.providers?.yandex_pay) ? (
        <div className="max-w-2xl mx-auto px-4 py-8">
          <PublicPaymentWidget
            kind="constructor-plans"
            token={token}
            paymentConfig={plan.payment}
            totalAmountRub={
              plan.pricing_summary?.total_amount_with_discount ??
              plan.pricing_summary?.total_amount ??
              plan.total_amount ??
              null
            }
            stages={(plan.stages ?? []).map((s) => ({
              title: s.title,
              total_amount: s.total_amount ?? null,
            }))}
            accent={resolveAccent(bootstrap, plan)}
            apiBase={bootstrap.apiBase}
          />
        </div>
      ) : plan?.payment?.cash_special ? (
        <div className="max-w-2xl mx-auto px-4 py-8">
          <div className="rounded-[24px] border border-amber-200 bg-amber-50 p-5 shadow-sm">
            <div className="flex items-start gap-3">
              <span className="text-xl">💵</span>
              <div>
                <p className="text-sm font-semibold text-amber-900">Спецусловия при оплате наличными</p>
                <p className="mt-1 text-sm text-amber-800">
                  По этому плану действуют особые условия при оплате наличными или через СБП.
                  Уточните детали у администратора клиники.
                </p>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <footer className="bg-white border-t border-gray-200 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <PatientPortalPromoBlock bootstrap={bootstrap} />
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            {bootstrap.clinic?.address && (
              <div>
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Адрес</h4>
                <p className="text-sm text-gray-700">{bootstrap.clinic.address}</p>
              </div>
            )}
            {bootstrap.clinic?.phone && (
              <div>
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Телефон</h4>
                <a href={`tel:${bootstrap.clinic.phoneTel}`} className="text-sm hover:underline" style={accentColor}>
                  {bootstrap.clinic.phone}
                </a>
              </div>
            )}
            {bootstrap.clinic?.email && (
              <div>
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Email</h4>
                <a href={`mailto:${bootstrap.clinic.email}`} className="text-sm hover:underline" style={accentColor}>
                  {bootstrap.clinic.email}
                </a>
              </div>
            )}
            {bootstrap.clinic?.website && (
              <div>
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Сайт</h4>
                <a
                  href={bootstrap.clinic.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm hover:underline"
                  style={accentColor}
                >
                  {bootstrap.clinic.websiteLabel || bootstrap.clinic.website}
                </a>
              </div>
            )}
          </div>
          {bootstrap.clinic?.legalName && (
            <div className="border-t border-gray-200 pt-6">
              <div className="text-center text-sm text-gray-500">
                <p>© {new Date().getFullYear()} {bootstrap.clinic.legalName}</p>
                {bootstrap.clinic.legalDetails && (
                  <p className="mt-1 text-xs">{bootstrap.clinic.legalDetails}</p>
                )}
                <p className="mt-2 text-xs text-gray-400">План лечения предоставлен для ознакомления</p>
              </div>
            </div>
          )}
        </div>
      </footer>

      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: white; }
        }
      `}</style>
    </div>
  )
}

function PlanContent({
  plan,
  bootstrap,
  onPublicEvent,
  accent,
  accentBg,
  accentLight,
  accentLighter,
  accentBorder,
  accentColor,
}: {
  plan: PublicTreatmentPlan
  bootstrap: TenantBootstrapModel
  onPublicEvent: (event: string, payload?: Record<string, unknown>) => void
  accent: string
  accentBg: React.CSSProperties
  accentLight: React.CSSProperties
  accentLighter: React.CSSProperties
  accentBorder: React.CSSProperties
  accentColor: React.CSSProperties
}) {
  const teeth = useMemo(() => collectTeeth(plan.stages), [plan.stages])
  const offerDeadline = resolveOfferDeadline(plan)

  const cashStages = useMemo(
    () => plan.stages.filter(s => (s as { cash_offer?: StageCashOffer | null }).cash_offer?.amount != null),
    [plan.stages],
  )
  const grandCash = useMemo(
    () => cashStages.reduce((sum, s) => sum + (((s as { cash_offer?: StageCashOffer | null }).cash_offer?.amount) ?? 0), 0),
    [cashStages],
  )

  let grandTotal = 0
  let grandDiscount = 0

  return (
    <div className="space-y-8">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 md:p-8">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
          <div className="flex-1">
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
              План лечения — {plan.patient_name ?? 'Не указан'}
            </h1>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">Пациент</p>
                <p className="font-semibold text-gray-900">{plan.patient_name ?? 'Не указан'}</p>
                {plan.patient_birthdate && (
                  <p className="text-sm text-gray-600 mt-1">
                    {formatDate(plan.patient_birthdate)} г.р.
                    {plan.patient_card_number ? `, Карта № ${plan.patient_card_number}` : null}
                  </p>
                )}
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">Лечащий врач</p>
                <p className="font-semibold text-gray-900">{plan.doctor_name ?? 'Не указан'}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">Дата создания</p>
                <p className="font-semibold text-gray-900">{formatDate(plan.created_at ?? plan.plan_date)}</p>
              </div>
            </div>
            {plan.description && (
              <div className="mt-4">
                <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-line">{plan.description}</p>
              </div>
            )}
            {plan.note && (
              <div className="mt-3 rounded-xl bg-gray-50 border border-gray-200 p-4">
                <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-line">{plan.note}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {!plan.hide_prices && offerDeadline ? (
        <OfferValidityCard plan={plan} deadline={offerDeadline} />
      ) : null}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-200 p-6 md:p-8">
          <h4 className="text-[13px] uppercase text-gray-500 tracking-wide mb-4 font-semibold">Зубная формула</h4>
          {teeth.display.length ? (
            <div className="flex flex-wrap gap-2 mb-4">
              {teeth.display.map((tooth) => (
                <span
                  key={tooth}
                  className="inline-flex items-center justify-center min-w-[28px] h-7 px-1.5 border rounded text-xs font-semibold"
                  style={{ ...accentLighter, ...accentBorder, ...accentColor }}
                >
                  {tooth}
                </span>
              ))}
            </div>
          ) : (
            <div className="mb-4 inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[12px] font-medium" style={{ ...accentBorder, ...accentColor, ...accentLighter }}>
              Вся полость рта
            </div>
          )}
          <div className="bg-transparent rounded-xl p-2 sm:p-4 overflow-x-auto flex justify-center">
            <ToothChart
              highlighted={teeth.numeric.length > 0 ? teeth.numeric : [11,12,13,14,15,16,17,18,21,22,23,24,25,26,27,28,31,32,33,34,35,36,37,38,41,42,43,44,45,46,47,48]}
              toothServices={teeth.servicesByTooth}
              primaryColor={accent}
              legend={false}
            />
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 md:p-8">
          <h4 className="text-[13px] uppercase text-gray-500 tracking-wide mb-4 font-semibold">Связь с клиникой</h4>
          <ClinicContactsCard bootstrap={bootstrap} accentBg={accentBg} accentColor={accentColor} onPublicEvent={onPublicEvent} />
        </div>
      </div>

      <div className="space-y-6">
        <h2 className="text-2xl font-bold text-gray-900">Этапы лечения</h2>
        {plan.stages.map((stage, stageIndex) => {
          const grouped = buildGroupedRows(stage.services)
          let stageTotal = 0
          let stageDiscount = 0
          for (const row of grouped) {
            const total = row.unitPrice * row.quantity
            const discount = row.discountedUnitPrice * row.quantity
            stageTotal += total
            stageDiscount += discount
          }
          grandTotal += stageTotal
          grandDiscount += stageDiscount
          const stageDoctorName = (stage as { doctor_name?: string | null }).doctor_name
          const cashOffer = (stage as { cash_offer?: StageCashOffer | null }).cash_offer ?? null
          const stageComment = ((stage as { comment?: string | null }).comment ?? stage.description ?? '').trim()
          const hasDiscount = stageDiscount < stageTotal
          const stageDiscountPercent = hasDiscount
            ? Math.round(((stageTotal - stageDiscount) / stageTotal) * 100)
            : 0

          return (
            <div key={stage.id} className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              <div
                className="px-6 py-5 border-b border-gray-200"
                style={{ background: 'linear-gradient(to right, var(--public-accent-soft), #f9fafb)' }}
              >
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                  <div className="flex items-start gap-4 flex-1">
                    <div
                      className="flex items-center justify-center w-12 h-12 text-white rounded-full font-bold text-xl shadow-sm flex-shrink-0"
                      style={accentBg}
                    >
                      {stageIndex + 1}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg md:text-xl font-bold text-gray-900">
                        Этап {stageIndex + 1}: {stage.title}
                      </h3>
                      {stageDoctorName && (
                        <p className="text-xs text-gray-500 mt-2">
                          Врач этапа: <span className="font-medium text-gray-800">{stageDoctorName}</span>
                        </p>
                      )}
                      {stageComment ? (
                        <div className="mt-3 rounded-2xl border border-gray-200 bg-white/70 px-4 py-3 text-sm leading-relaxed text-gray-700 shadow-sm">
                          <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-500">Комментарий к этапу</p>
                          <p className="whitespace-pre-line">{stageComment}</p>
                        </div>
                      ) : null}
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-4 lg:flex-nowrap">
                    <span
                      className="px-4 py-2 text-sm font-medium rounded-full border"
                      style={{ ...accentLighter, ...accentBorder, ...accentColor }}
                    >
                      {statusLabel(stage.status)}
                    </span>
                  </div>
                </div>
              </div>
              <div className="p-4 md:p-6">
                <h4 className="text-base md:text-lg font-semibold text-gray-900 mb-4">Услуги этапа</h4>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-200">
                        <th className="px-3 md:px-4 py-3 text-left text-xs md:text-sm font-semibold text-gray-700">
                          № Зуба
                        </th>
                        <th className="px-3 md:px-4 py-3 text-left text-xs md:text-sm font-semibold text-gray-700">
                          Услуга
                        </th>
                        {!plan.hide_prices && (
                          <th className="px-3 md:px-4 py-3 text-right text-xs md:text-sm font-semibold text-gray-700">
                            Цена, ₽
                          </th>
                        )}
                        <th className="px-3 md:px-4 py-3 text-right text-xs md:text-sm font-semibold text-gray-700">
                          Кол-во
                        </th>
                        {!plan.hide_prices && (
                          <th className="px-3 md:px-4 py-3 text-right text-xs md:text-sm font-semibold text-gray-700">
                            Сумма, ₽
                          </th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {grouped.map((row, idx) => {
                        const teethList = row.teeth.length
                          ? row.teeth.join(', ')
                          : 'Вся полость'
                        const totalPrice = row.unitPrice * row.quantity
                        const discountPrice = row.discountedUnitPrice * row.quantity
                        const rowDiscount = discountPrice < totalPrice
                        return (
                          <tr key={idx} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                            <td className="px-3 md:px-4 py-3 text-xs md:text-sm text-gray-600">
                              {teethList === 'Вся полость' ? (
                                <span className="text-gray-400">Вся полость</span>
                              ) : (
                                teethList
                              )}
                            </td>
                            <td className="px-3 md:px-4 py-3 text-xs md:text-sm font-medium text-gray-900 max-w-md">
                              {row.name}
                            </td>
                            {!plan.hide_prices && (
                              <td className="px-3 md:px-4 py-3 text-xs md:text-sm text-right text-gray-600">
                                {rowDiscount ? (
                                  <>
                                    <div className="line-through text-gray-400 text-[11px]">
                                      {row.unitPrice.toLocaleString('ru-RU')}
                                    </div>
                                    <div className="font-semibold" style={accentColor}>
                                      {row.discountedUnitPrice.toLocaleString('ru-RU')}
                                    </div>
                                  </>
                                ) : (
                                  row.unitPrice.toLocaleString('ru-RU')
                                )}
                              </td>
                            )}
                            <td className="px-3 md:px-4 py-3 text-xs md:text-sm text-right text-gray-600 font-medium">
                              {row.quantity}
                            </td>
                            {!plan.hide_prices && (
                              <td className="px-3 md:px-4 py-3 text-xs md:text-sm text-right font-medium">
                                {rowDiscount ? (
                                  <>
                                    <div className="line-through text-gray-400 text-[11px]">
                                      {totalPrice.toLocaleString('ru-RU')}
                                    </div>
                                    <div style={accentColor}>{discountPrice.toLocaleString('ru-RU')}</div>
                                  </>
                                ) : (
                                  totalPrice.toLocaleString('ru-RU')
                                )}
                              </td>
                            )}
                          </tr>
                        )
                      })}
                      {!plan.hide_prices && (
                        <tr className="border-t-2 border-gray-200">
                          <td colSpan={3} className="px-3 md:px-4 py-4" />
                          <td className="px-3 md:px-4 py-4 text-right font-semibold text-gray-900 text-sm">
                            Итого по этапу{hasDiscount ? ` со скидкой ${stageDiscountPercent}%` : ''}:
                          </td>
                          <td className="px-3 md:px-4 py-4 text-right font-semibold text-base md:text-lg" style={accentColor}>
                            {hasDiscount ? (
                              <>
                                <div className="line-through text-gray-400 text-xs">
                                  {stageTotal.toLocaleString('ru-RU')} ₽
                                </div>
                                <div>{stageDiscount.toLocaleString('ru-RU')} ₽</div>
                              </>
                            ) : (
                              <>{stageTotal.toLocaleString('ru-RU')} ₽</>
                            )}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                {!plan.hide_prices && cashOffer && (cashOffer.note || cashOffer.amount != null) ? (
                  <CashOfferCallout offer={cashOffer} accent={accent} />
                ) : null}
              </div>
            </div>
          )
        })}

        {plan.installment?.enabled && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 md:p-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Рассрочка</h3>
            <InstallmentSummary installment={plan.installment} accent={accent} accentColor={accentColor} hidePrices={Boolean(plan.hide_prices)} />
          </div>
        )}

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 md:p-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div>
              <p className="text-sm font-semibold text-gray-900">План лечения согласован:</p>
              <div className="flex flex-wrap gap-8 mt-4">
                <div>
                  <p className="text-xs uppercase text-gray-500 mb-1">Доктор</p>
                  <p className="font-medium text-gray-900">{plan.doctor_name ?? 'Не указан'}</p>
                </div>
                <div>
                  <p className="text-xs uppercase text-gray-500 mb-1">Пациент</p>
                  <p className="font-medium text-gray-900">{plan.patient_name ?? 'Не указан'}</p>
                </div>
              </div>
            </div>
            {!plan.hide_prices && (
              <div className="text-right">
                <p className="text-sm text-gray-500 mb-1">
                  Итого{grandDiscount < grandTotal ? ' со скидкой' : ''}:
                </p>
                {grandDiscount < grandTotal && (
                  <p className="text-sm line-through text-gray-400">
                    {grandTotal.toLocaleString('ru-RU')} ₽
                  </p>
                )}
                <p className="text-2xl font-bold" style={accentColor}>
                  {(grandDiscount < grandTotal ? grandDiscount : grandTotal).toLocaleString('ru-RU')} ₽
                </p>
                {cashStages.length > 0 && !plan.plan_cash_offer && (
                  <div className="mt-3 text-right">
                    <p className="text-xs text-gray-500">Итого за наличные:</p>
                    <p className="text-xl font-bold text-green-700">{grandCash.toLocaleString('ru-RU')} ₽</p>
                    <p className="text-[11px] text-gray-400">по {cashStages.length} этап(ам) с нал. ценой</p>
                  </div>
                )}
              </div>
            )}
            {!plan.hide_prices && plan.plan_cash_offer &&
              (plan.plan_cash_offer.note || plan.plan_cash_offer.amount != null) && (
              <div className="mt-4">
                <CashOfferCallout offer={plan.plan_cash_offer as StageCashOffer} accent={accent} />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

function OfferValidityCard({ plan, deadline }: { plan: PublicTreatmentPlan; deadline: string }) {
  const total = plan.pricing_summary?.total_amount_with_discount ?? plan.pricing_summary?.total_amount ?? plan.total_amount ?? null
  const futureTotal = typeof total === 'number' ? Math.round(total * 1.3) : null

  return (
    <section className="rounded-2xl border border-[var(--public-accent-line)] bg-gradient-to-r from-[var(--public-accent-soft)] to-white p-5 shadow-sm print:break-inside-avoid">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Фиксация предложения</p>
          <h2 className="mt-1 text-2xl font-bold text-gray-900">
            Цена зафиксирована до {formatDate(deadline)}
          </h2>
          <OfferDeadlineClock deadline={deadline} />
        </div>
        {total !== null ? (
          <div className="grid min-w-[260px] grid-cols-2 overflow-hidden rounded-xl border border-white/70 bg-white shadow-sm">
            <div className="p-4">
              <p className="text-xs text-gray-500">Сейчас</p>
              <p className="mt-1 text-xl font-bold text-gray-900">{formatCurrency(total)}</p>
            </div>
            <div className="border-l border-gray-100 bg-amber-50 p-4">
              <p className="text-xs text-amber-700">Через 6 месяцев</p>
              <p className="mt-1 text-xl font-bold text-amber-800">{formatCurrency(futureTotal)}</p>
              <p className="mt-1 text-[11px] text-amber-700">риск +30% к стоимости или объёму лечения</p>
            </div>
          </div>
        ) : null}
      </div>
    </section>
  )
}

function MessengerIcon({ type }: { type: 'whatsapp' | 'telegram' | 'max' }) {
  if (type === 'whatsapp') {
    return (
      <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
        <path d="M20.52 3.48A11.8 11.8 0 0012.13 0C5.6 0 .29 5.31.29 11.84c0 2.09.55 4.13 1.59 5.93L.19 24l6.38-1.67a11.82 11.82 0 005.56 1.42h.01c6.53 0 11.84-5.31 11.84-11.84 0-3.16-1.23-6.14-3.46-8.43zM12.14 21.75h-.01a9.8 9.8 0 01-5-1.37l-.36-.21-3.78.99 1.01-3.68-.24-.38a9.83 9.83 0 01-1.5-5.26c0-5.43 4.42-9.84 9.86-9.84a9.8 9.8 0 016.96 2.89 9.79 9.79 0 012.89 6.96c0 5.43-4.42 9.85-9.84 9.85zm5.4-7.38c-.3-.15-1.75-.86-2.02-.96-.27-.1-.47-.15-.67.15-.2.3-.77.96-.94 1.16-.17.2-.35.22-.65.07-.3-.15-1.25-.46-2.38-1.47-.88-.78-1.47-1.75-1.64-2.05-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.67-1.61-.92-2.2-.24-.58-.49-.5-.67-.51l-.57-.01c-.2 0-.52.07-.79.37-.27.3-1.04 1.02-1.04 2.48s1.07 2.88 1.22 3.08c.15.2 2.11 3.22 5.1 4.51.71.31 1.27.49 1.7.63.71.23 1.36.2 1.87.12.57-.09 1.75-.72 2-1.41.25-.69.25-1.29.17-1.41-.07-.13-.27-.2-.57-.35z" />
      </svg>
    )
  }

  if (type === 'max') {
    return <img src={`${import.meta.env.BASE_URL}max-logo.png`} alt="" className="h-5 w-5 rounded-[5px]" aria-hidden />
  }

  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M21.94 4.16c.31-1.02-.67-1.87-1.58-1.5L2.48 9.96c-1.04.42-1.02 1.9.03 2.29l4.56 1.68 1.74 5.58c.32 1.02 1.62 1.32 2.35.54l2.5-2.66 4.62 3.41c.85.63 2.06.16 2.29-.87l3.36-15.77zM8.1 13.12l9.54-5.88-7.88 7.45-.31 3.28-1.35-4.85z" />
    </svg>
  )
}

function MessengerButton({
  href,
  label,
  type,
  className,
  onPublicEvent,
}: {
  href: string
  label: string
  type: 'whatsapp' | 'telegram' | 'max'
  className: string
  onPublicEvent?: (event: string, payload?: Record<string, unknown>) => void
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => onPublicEvent?.('click', { target: `messenger:${type}`, channel: type, label, href })}
      className={`flex min-h-10 items-center justify-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold text-white transition-colors ${className}`}
    >
      <MessengerIcon type={type} />
      {label}
    </a>
  )
}

function ClinicContactsCard({
  bootstrap,
  accentBg,
  accentColor,
  onPublicEvent,
}: {
  bootstrap: TenantBootstrapModel
  accentBg: React.CSSProperties
  accentColor: React.CSSProperties
  onPublicEvent: (event: string, payload?: Record<string, unknown>) => void
}) {
  const clinic = bootstrap.clinic
  if (!clinic) return null

  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(
    clinic.whatsappUrl || `tel:${clinic.phoneTel}`,
  )}`

  return (
    <div className="text-center">
      <p className="text-sm text-gray-600 mb-3">Отсканируйте, чтобы написать</p>
      <div className="flex justify-center mb-4">
        <img
          src={qrSrc}
          alt="QR-код для связи"
          width={160}
          height={160}
          className="rounded-md border border-gray-200"
          loading="lazy"
        />
      </div>
      <div className="space-y-2">
        {clinic.phone && (
          <a
            href={`tel:${clinic.phoneTel}`}
            onClick={() => onPublicEvent('click', { target: 'phone', channel: 'phone', label: clinic.phone })}
            className="flex items-center justify-center gap-2 hover:opacity-80 font-medium"
            style={accentColor}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
              />
            </svg>
            {clinic.phone}
          </a>
        )}
        <p className="text-xs text-gray-500 text-center">WhatsApp, Telegram, MAX</p>
        <div className="grid grid-cols-1 gap-2 pt-2 sm:grid-cols-2">
          {clinic.whatsappUrl && (
            <MessengerButton
              href={clinic.whatsappUrl}
              label="WhatsApp"
              type="whatsapp"
              className="bg-green-600 hover:bg-green-700"
              onPublicEvent={onPublicEvent}
            />
          )}
          {clinic.telegramUrl && (
            <MessengerButton
              href={clinic.telegramUrl}
              label="Telegram"
              type="telegram"
              className="bg-sky-500 hover:bg-sky-600"
              onPublicEvent={onPublicEvent}
            />
          )}
          {clinic.telegramNotifyUrl && (
            <MessengerButton
              href={clinic.telegramNotifyUrl}
              label="Telegram бот"
              type="telegram"
              className="bg-blue-600 hover:bg-blue-700"
              onPublicEvent={onPublicEvent}
            />
          )}
          {clinic.maxBotUrl && (
            <MessengerButton
              href={clinic.maxBotUrl}
              label="MAX бот"
              type="max"
              className="bg-violet-600 hover:bg-violet-700"
              onPublicEvent={onPublicEvent}
            />
          )}
        </div>
        {clinic.address && (
          <p className="text-xs text-gray-500 text-center pt-3 leading-relaxed">{clinic.address}</p>
        )}
        {/* hint to satisfy unused-prop check */}
        <span style={{ display: 'none', ...accentBg }} aria-hidden />
      </div>
    </div>
  )
}

function PatientPortalPromoBlock({ bootstrap }: { bootstrap: TenantBootstrapModel }) {
  const portalUrl = bootstrap.clinic?.patientPortalUrl
  if (!portalUrl) return null

  return (
    <section className="mb-8 overflow-hidden rounded-3xl border border-[var(--public-accent-line)] bg-gradient-to-br from-white via-white to-[var(--public-accent-softer)] p-5 shadow-sm md:p-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[var(--public-accent)]">
            Личный кабинет пациента
          </p>
          <h3 className="mt-2 text-xl font-bold text-gray-900">
            Ваши приемы, индивидуальные скидки и программа лояльности
          </h3>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-gray-600">
            Откройте кабинет, чтобы видеть записи, рекомендации врача, персональные предложения и материалы клиники.
          </p>
        </div>
        <a
          href={portalUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex shrink-0 items-center justify-center rounded-xl bg-[var(--public-primary)] px-5 py-3 text-sm font-semibold text-white shadow-sm transition-opacity hover:opacity-90"
        >
          Перейти в кабинет
          <span className="ml-2 text-xs opacity-80">{bootstrap.clinic.patientPortalLabel ?? ''}</span>
        </a>
      </div>
    </section>
  )
}

function InstallmentSummary({
  installment,
  accent,
  accentColor,
  hidePrices,
}: {
  installment: NonNullable<PublicTreatmentPlan['installment']>
  accent: string
  accentColor: React.CSSProperties
  hidePrices: boolean
}) {
  const months = installment.months ?? 0
  const amount = installment.amount ?? 0
  const monthly = months > 0 ? Math.round(amount / months) : 0
  const schedule = (installment.schedule ?? []).slice().sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
  return (
    <div className="space-y-5">
      <div className="grid gap-4 md:grid-cols-3">
        <div className="rounded-md border border-gray-200 bg-gray-50 p-4">
          <p className="text-xs text-gray-500 mb-1">Сумма рассрочки</p>
          <p className="text-xl font-bold" style={accentColor}>
            {hidePrices ? 'По согласованию' : `${amount.toLocaleString('ru-RU')} ₽`}
          </p>
        </div>
        <div className="rounded-md border border-gray-200 bg-gray-50 p-4">
          <p className="text-xs text-gray-500 mb-1">Срок</p>
          <p className="text-xl font-bold text-gray-900">{months > 0 ? `${months} мес.` : 'По графику'}</p>
        </div>
        <div className="rounded-md border border-gray-200 bg-gray-50 p-4">
          <p className="text-xs text-gray-500 mb-1">Ежемесячно</p>
          <p className="text-xl font-bold text-gray-900">{!hidePrices && monthly > 0 ? `${monthly.toLocaleString('ru-RU')} ₽` : 'По графику'}</p>
        </div>
      </div>
      {installment.note && (
        <p className="text-sm text-gray-600">{installment.note}</p>
      )}
      {schedule.length > 0 ? (
        <div className="space-y-3">
          <p className="text-sm font-semibold text-gray-900">График платежей</p>
          <div className="relative space-y-3 pl-5">
            <div className="absolute bottom-3 left-2 top-3 w-px bg-gray-200" />
            {schedule.map((payment, index) => (
              <div key={`${payment.id ?? index}-${payment.title}`} className="relative rounded-xl border border-gray-200 bg-gray-50 p-4">
                <span
                  className="absolute -left-[17px] top-5 h-4 w-4 rounded-full border-2 bg-white"
                  style={{ borderColor: accent }}
                  aria-hidden
                />
                <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <p className="font-semibold text-gray-900">{payment.title}</p>
                    {payment.stage_title ? (
                      <p className="text-xs text-gray-500">Этап: {payment.stage_title}</p>
                    ) : null}
                    {payment.note ? <p className="mt-1 text-sm text-gray-600">{payment.note}</p> : null}
                  </div>
                  <div className="text-left sm:text-right">
                    <p className="text-xs uppercase tracking-wide text-gray-500">
                      {payment.due_date ? `до ${formatDate(payment.due_date)}` : 'дата не указана'}
                    </p>
                    {!hidePrices ? (
                      <p className="text-lg font-bold" style={accentColor}>
                        {formatCurrency(payment.amount ?? null)}
                      </p>
                    ) : null}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <p className="text-sm text-gray-500">График платежей уточняется менеджером.</p>
      )}
    </div>
  )
}

interface CountdownParts {
  days: number
  hours: number
  minutes: number
  seconds: number
  expired: boolean
}

function calcCountdown(deadline: string): CountdownParts | null {
  const target = new Date(deadline)
  if (!Number.isFinite(target.getTime())) return null
  const diffMs = target.getTime() - Date.now()
  if (diffMs <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0, expired: true }
  const totalSeconds = Math.floor(diffMs / 1000)
  return {
    days: Math.floor(totalSeconds / 86400),
    hours: Math.floor((totalSeconds % 86400) / 3600),
    minutes: Math.floor((totalSeconds % 3600) / 60),
    seconds: totalSeconds % 60,
    expired: false,
  }
}

function useCountdown(deadline: string | null | undefined): CountdownParts | null {
  const [parts, setParts] = useState<CountdownParts | null>(() => (deadline ? calcCountdown(deadline) : null))
  useEffect(() => {
    if (!deadline) { setParts(null); return }
    setParts(calcCountdown(deadline))
    const id = window.setInterval(() => setParts(calcCountdown(deadline)), 1000)
    return () => window.clearInterval(id)
  }, [deadline])
  return parts
}

function CountdownDigit({ value, label }: { value: number; label: string }) {
  const str = String(value).padStart(2, '0')
  return (
    <div className="flex flex-col items-center">
      <div className="flex h-11 w-12 items-center justify-center overflow-hidden rounded-xl border border-[var(--public-accent-line)] bg-white/90 shadow-sm md:h-14 md:w-16">
        <span key={str} className="digit-tick text-2xl font-bold tabular-nums text-gray-900 md:text-3xl">{str}</span>
      </div>
      <span className="mt-1 text-[10px] font-medium uppercase tracking-wide text-gray-500">{label}</span>
    </div>
  )
}

function CountdownColon() {
  return (
    <div className="flex flex-col items-center justify-center pb-5">
      <span className="text-xl font-bold text-[var(--public-accent)] opacity-70">:</span>
    </div>
  )
}

function OfferDeadlineClock({ deadline }: { deadline?: string | null }) {
  const parts = useCountdown(deadline)

  if (!deadline) return null

  return (
    <div className="mt-3">
      {parts?.expired ? (
        <div className="inline-flex items-center gap-2 rounded-full border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-medium text-red-700">
          Срок предложения истёк
        </div>
      ) : parts ? (
        <div className="flex items-end gap-1">
          {parts.days > 0 && (
            <>
              <CountdownDigit value={parts.days} label="дней" />
              <CountdownColon />
            </>
          )}
          <CountdownDigit value={parts.hours} label="часов" />
          <CountdownColon />
          <CountdownDigit value={parts.minutes} label="минут" />
          <CountdownColon />
          <CountdownDigit value={parts.seconds} label="секунд" />
        </div>
      ) : null}
      <p className="mt-2 text-xs text-gray-500">
        до <span className="font-semibold">{formatDate(deadline)}</span>
      </p>
    </div>
  )
}

function CashOfferCallout({ offer, accent }: { offer: StageCashOffer; accent: string }) {
  const formattedAmount = offer.amount != null
    ? offer.amount.toLocaleString('ru-RU')
    : null
  const methodLabel = offer.method && offer.method.trim().length > 0
    ? offer.method.trim()
    : 'оплата наличными'

  return (
    <div
      className="mt-5 rounded-2xl border border-[var(--public-accent-line)] bg-gradient-to-r from-[var(--public-accent-soft)] to-[var(--public-accent-softer)] px-5 py-4 shadow-sm print:break-inside-avoid"
    >
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div className="flex items-start gap-3">
          <span
            className="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-white shadow"
            style={{ background: 'var(--public-accent)' }}
            aria-hidden
          >
            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 1.12-3 2.5S10.343 13 12 13s3 1.12 3 2.5S13.657 18 12 18m0-10v10m0 0V20m0-12V6m9 6a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </span>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-[var(--public-accent)]">
              Специальная цена · {methodLabel}
            </p>
            {offer.note ? (
              <p className="mt-1 text-sm text-gray-800 leading-relaxed whitespace-pre-line">
                {offer.note}
              </p>
            ) : null}
            <OfferDeadlineClock deadline={offer.deadline} />
          </div>
        </div>
        {formattedAmount ? (
          <div className="md:text-right">
            <p className="text-xs uppercase text-gray-500">Цена этапа</p>
            <p className="text-2xl font-bold text-[var(--public-accent)]">
              {formattedAmount} ₽
            </p>
          </div>
        ) : null}
        <span style={{ display: 'none', color: accent }} aria-hidden />
      </div>
    </div>
  )
}
